import { useState, useEffect, useRef } from "react";
import {
  extractArtistThemeFromImageUrl,
  type ExtractedTheme,
} from "@/utils/extractArtistThemeFromImageUrl";
import { getArtistMaterialTheme } from "@/utils/getArtistMaterialTheme";

/**
 * Derives a fallback theme from a hex color
 */
function getFallbackTheme(fallbackHex: string): ExtractedTheme {
  const theme = getArtistMaterialTheme(fallbackHex);
  return {
    accent: theme.primary,
    tint: theme.tint,
    onAccent: theme.onPrimary,
    ink: theme.primaryInk,
  };
}

/**
 * Gets cached theme from localStorage (synchronous read)
 */
function getCachedTheme(
  imageUrl: string,
  cacheKeyPrefix = "artistTheme:",
): ExtractedTheme | null {
  if (!imageUrl || typeof window === "undefined") {
    return null;
  }

  try {
    const cacheKey = `${cacheKeyPrefix}${imageUrl}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      return {
        accent: parsed.accent,
        tint: parsed.tint,
        onAccent: parsed.onAccent,
        ink: parsed.ink,
      };
    }
  } catch {
    // Cache read failed
  }

  return null;
}

/**
 * Hook to extract artist theme from avatar image URL
 */
export function useArtistThemeFromAvatar(
  imageUrl?: string | null,
  fallbackHex = "#1a1a1a",
  key?: string | null, // Optional key to force reset when it changes (e.g., creatorId)
  forceRegen?: boolean, // If true, bypass localStorage cache and force regeneration
): {
  theme: ExtractedTheme | null;
  isLoading: boolean;
} {
  const [theme, setTheme] = useState<ExtractedTheme | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const lastUrlRef = useRef<string | null>(null);
  const lastKeyRef = useRef<string | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    // Reset cancelled flag when effect runs
    cancelledRef.current = false;

    // Track if key changed to force re-processing even if URL is the same
    const keyChanged = key && lastKeyRef.current !== key;

    // Reset if key changed (e.g., navigating to different creator or page refresh)
    // Also reset on initial mount when key is provided but lastKeyRef is null (page refresh)
    if (keyChanged) {
      lastUrlRef.current = null;
      lastKeyRef.current = key;
      setTheme(null);
      setIsLoading(true);

      // Clear cache for the current URL when key changes to force fresh extraction
      // This handles the case where avatarVersion changes but URL might be the same
      if (imageUrl) {
        try {
          localStorage.removeItem(`artistTheme:${imageUrl}`);
        } catch {
          // Ignore localStorage errors
        }
      }
    }

    if (!imageUrl) {
      // No image URL, use fallback
      setTheme(getFallbackTheme(fallbackHex));
      setIsLoading(false);
      lastUrlRef.current = null;
      return;
    }

    // Check if URL changed - if not, skip processing UNLESS key changed
    // When key changes (e.g., avatarVersion increments), we want to force re-extraction
    // even if the URL appears to be the same (in case backend returns same URL for new image)
    const urlChanged = lastUrlRef.current !== imageUrl;
    if (!urlChanged && !keyChanged) {
      return;
    }

    // URL changed or key changed - reset theme and show loading to prevent flicker
    setTheme(null);
    setIsLoading(true);

    // Update ref to current URL
    lastUrlRef.current = imageUrl;

    // Check cache first (synchronous) - but skip cache if key changed or forceRegen is true
    const shouldSkipCache = keyChanged || forceRegen;
    const cached = shouldSkipCache ? null : getCachedTheme(imageUrl);
    if (cached) {
      // Set theme synchronously to prevent flicker
      setTheme(cached);
      setIsLoading(false);

      // Store accent color for backwards compatibility
      try {
        localStorage.setItem("artistColor", cached.accent);
      } catch {
        // Ignore localStorage errors
      }

      return;
    }

    // No cache - start extraction for this URL
    setIsLoading(true);

    // Start async extraction with forceRegen if key changed or forceRegen is true
    extractArtistThemeFromImageUrl(imageUrl, {
      forceRegen: shouldSkipCache,
    })
      .then((extractedTheme) => {
        // Check if this effect was cancelled (URL changed or unmounted)
        if (cancelledRef.current || lastUrlRef.current !== imageUrl) {
          return;
        }

        if (extractedTheme) {
          setTheme(extractedTheme);

          // Store accent color for backwards compatibility
          try {
            localStorage.setItem("artistColor", extractedTheme.accent);
          } catch {
            // Ignore localStorage errors
          }
        } else {
          // Extraction failed, use fallback
          setTheme(getFallbackTheme(fallbackHex));
        }

        setIsLoading(false);
      })
      .catch(() => {
        // Error already handled in extractArtistThemeFromImageUrl
        if (!cancelledRef.current && lastUrlRef.current === imageUrl) {
          setTheme(getFallbackTheme(fallbackHex));
          setIsLoading(false);
        }
      });

    // Cleanup: mark as cancelled if URL changes or component unmounts
    return () => {
      cancelledRef.current = true;
    };
  }, [imageUrl, fallbackHex, key, forceRegen]);

  return { theme, isLoading };
}
